
import { LogData } from '@gentrometer/shared';
import React, { useEffect, useMemo, useState } from 'react';
import { genLogs } from '../../utils/api';
import './Logs.css';
import classNames from 'classnames';

export default function Logs() {
  const [logsByPage, setLogsByPage] = useState<Map<number, Array<LogData>>>(new Map());
  const [currentLog, setCurrentLog] = useState<string | null>(null);
  const [wrapEnabled, setWrapEnabled] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [moreLogs, setMoreLogs] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Concatenate all loaded pages into a single list
  const logs = useMemo(() => {
    const allLogs: Array<LogData> = [];
    const sortedPages = Array.from(logsByPage.keys()).sort((a, b) => a - b);
    for (const page of sortedPages) {
      const pageLogs = logsByPage.get(page) ?? [];
      allLogs.push(...pageLogs);
    }
    return allLogs;
  }, [logsByPage]);

  const logsByFilename = useMemo(() => {
    const map = new Map<string, LogData>();
    for (const log of logs) {
      map.set(log.logFilename, log);
    }
    return map;
  }, [logs]);

  // Fetch page when currentPage changes and page is not already loaded
  useEffect(() => {
    if (logsByPage.has(currentPage)) {
      return; // Page already loaded
    }

    const loadPage = async () => {
      setLoading(true);
      try {
        const response = await genLogs(currentPage);
        const pageLogs = response?.logs ?? [];
        setLogsByPage(prev => {
          const newMap = new Map(prev);
          newMap.set(currentPage, pageLogs);
          return newMap;
        });
        setMoreLogs(response?.moreLogs ?? false);
      } catch (error) {
        console.error(`Error loading page ${currentPage}:`, error);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadNextPage = () => {
    if (loading || !moreLogs) return;
    setCurrentPage(prev => prev + 1);
  };

  // Auto-select first log file when logs are loaded
  useEffect(() => {
    if (logs.length > 0 && currentLog === null) {
      setCurrentLog(logs[0].logFilename);
    }
  }, [logs, currentLog]);

  const logContent = currentLog != null ? logsByFilename.get(currentLog)?.content ?? '' : '';

  return (
    <div className="logs-page">
      <h1>Logs</h1>
      <div className="logs-container">
        <div className="log-file-container">
          <div className="log-file-header">
            <span>{currentLog ?? 'Select a log file...'}</span>
            {currentLog && (
              <label className="wrap-toggle">
                <input
                  type="checkbox"
                  checked={wrapEnabled}
                  onChange={(e) => setWrapEnabled(e.target.checked)}
                />
                <span>Wrap</span>
              </label>
            )}
          </div>
          <pre className={classNames('log-file-content', {
            'log-file-content-wrapped': wrapEnabled
          })}>
            {logContent}
          </pre>
        </div>
        <div className="log-file-selector">
          {logs && logs.map(log => (
            <div 
              key={log.logFilename}
              className={classNames('log-file-name', {
                'log-file-name-active': currentLog === log.logFilename
              })} 
              onClick={() => setCurrentLog(log.logFilename)}>
              {log.logFilename}
            </div>
          ))}
          {moreLogs && (
            <button 
              className="load-more-button"
              onClick={loadNextPage}
              disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}